/** Rules engine types for funnel targeting and actions */

export type TriggerType =
  | 'cart_value'
  | 'product_in_cart'
  | 'product_count'
  | 'customer_tag'
  | 'geo_location'
  | 'device_type'
  | 'traffic_source'
  | 'time_on_page'
  | 'page_url'

export type TriggerOperator =
  | 'equals'
  | 'not_equals'
  | 'greater_than'
  | 'less_than'
  | 'contains'
  | 'not_contains'
  | 'in'
  | 'not_in'

export type ActionType =
  | 'show_step'
  | 'skip_step'
  | 'redirect'
  | 'apply_discount'
  | 'set_variant'
  | 'log_event'

export type RuleLogic = 'AND' | 'OR'
export type MatchMode = 'any' | 'all'
export type FunnelRuleType = 'product' | 'order_value' | 'product_count' | 'tag'
export type FunnelRuleComparator = 'any' | 'all' | 'notany' | 'notall' | 'lt' | 'le' | 'gt' | 'ge' | 'eq' | 'ne'

export interface RuleTrigger {
  id: string
  type: TriggerType
  operator: TriggerOperator
  value: string | number | string[]
}

export interface RuleAction {
  id: string
  type: ActionType
  target?: string
  config?: Record<string, string | number | boolean>
}

export interface FunnelRule {
  id: string
  name: string
  logic: RuleLogic
  triggers: RuleTrigger[]
  actions: RuleAction[]
  isActive: boolean
  priority: number
}

export interface FunnelRuleRecord {
  id: string
  tenantId: string
  funnelId: string
  ruleType: FunnelRuleType
  comparator: FunnelRuleComparator
  value?: string
  productIds?: string[]
  isEnabled: boolean
  createdAt: string
  updatedAt: string
  deletedAt?: string | null
}

export const TRIGGER_TYPE_LABELS: Record<TriggerType, string> = {
  cart_value: 'Cart Value',
  product_in_cart: 'Product in Cart',
  product_count: 'Product Count',
  customer_tag: 'Customer Tag',
  geo_location: 'Geo Location',
  device_type: 'Device Type',
  traffic_source: 'Traffic Source',
  time_on_page: 'Time on Page',
  page_url: 'Page URL',
}

export const TRIGGER_OPERATOR_LABELS: Record<TriggerOperator, string> = {
  equals: 'Equals',
  not_equals: 'Not Equals',
  greater_than: 'Greater Than',
  less_than: 'Less Than',
  contains: 'Contains',
  not_contains: 'Not Contains',
  in: 'In',
  not_in: 'Not In',
}

export const ACTION_TYPE_LABELS: Record<ActionType, string> = {
  show_step: 'Show Step',
  skip_step: 'Skip Step',
  redirect: 'Redirect',
  apply_discount: 'Apply Discount',
  set_variant: 'Set Variant',
  log_event: 'Log Event',
}

/** Operators available per trigger type */
export const TRIGGER_OPERATORS: Record<TriggerType, TriggerOperator[]> = {
  cart_value: ['greater_than', 'less_than', 'equals'],
  product_in_cart: ['equals', 'in', 'not_in'],
  product_count: ['greater_than', 'less_than', 'equals'],
  customer_tag: ['equals', 'contains', 'in'],
  geo_location: ['equals', 'in', 'not_in'],
  device_type: ['equals', 'not_equals'],
  traffic_source: ['equals', 'contains'],
  time_on_page: ['greater_than', 'less_than'],
  page_url: ['equals', 'contains', 'not_contains'],
}
